import React from 'react';
import { useDraggable, useDndMonitor } from '@dnd-kit/core';
import { useExerciseDnd, type PlacedChipItem } from '../dnd';
import { isUndoDrop } from './chip-drop';

export interface PlacedInputChipProps {
  /** Input id this chip currently occupies (also used in droppedSources). */
  widgetId: string;
  value: string;
  /** Extra classes appended after the base `inline-input-chip`. */
  className?: string;
  /** Remove the chip and return its option to the bank. */
  onRemove: () => void;
}

/**
 * A value placed into an input via drag-drop, shown as a chip.
 * - Draggable: drag onto another input to move it, or onto empty space to undo.
 * - Click (without dragging) still removes it.
 *
 * Shared by InlineInput and FractionInput (one chip per fraction slot).
 */
export const PlacedInputChip: React.FC<PlacedInputChipProps> = ({ widgetId, value, className = '', onRemove }) => {
  const { droppedSources } = useExerciseDnd();
  const sourceOptionId = droppedSources.get(widgetId);
  const dragId = `${widgetId}__chip`;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId,
    data: { type: 'placed-chip', widgetId, label: value, sourceOptionId } satisfies PlacedChipItem,
  });

  // Dropped on empty space (no valid target) → undo. Drops onto an input are
  // handled by that input's drop handler, which clears this source slot.
  useDndMonitor({
    onDragEnd(event) {
      if (isUndoDrop(event, dragId)) onRemove();
    },
  });

  return (
    <button
      ref={setNodeRef}
      className={`inline-input-chip ${className}${isDragging ? ' dragging' : ''}`.trim()}
      // Only a real pointer click removes (detail >= 1). Keyboard-synthesized
      // clicks have detail === 0 — ignore them so dnd-kit's KeyboardSensor can
      // own Space/Enter to start a drag instead of removing the chip.
      onClick={(e) => { if (e.detail !== 0) onRemove(); }}
      title="Drag to move, or click to remove"
      style={{ touchAction: 'none', cursor: 'grab' }}
      {...listeners}
      {...attributes}
    >
      {value}
    </button>
  );
};
